import React from "react";
import type { ElementType, ReactElement, ButtonHTMLAttributes } from "react";

// import { button } from './Button.css';
import { contract, Frame } from "@react95/core";

export type ButtonProps = {
  as?: ElementType;
  noShadow?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function CustomButton({
  as: Component = "button",
  style,
  ...props
}: ButtonProps): ReactElement {
  const [hover, setHover] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const [focus, setFocus] = React.useState(false);

  const {
    disabled,
    onMouseEnter: userOnMouseEnter,
    onMouseLeave: userOnMouseLeave,
    onMouseDown: userOnMouseDown,
    onMouseUp: userOnMouseUp,
    onFocus: userOnFocus,
    onBlur: userOnBlur,
    onKeyDown: userOnKeyDown,
    onKeyUp: userOnKeyUp,
    noShadow,
  } = props as any;

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled) setHover(true);
      userOnMouseEnter?.(e as any);
    },
    [disabled, userOnMouseEnter],
  );

  const handleMouseLeave = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setHover(false);
      setPressed(false);
      userOnMouseLeave?.(e as any);
    },
    [userOnMouseLeave],
  );

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled) setPressed(true);
      userOnMouseDown?.(e as any);
    },
    [disabled, userOnMouseDown],
  );

  const handleMouseUp = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setPressed(false);
      userOnMouseUp?.(e as any);
    },
    [userOnMouseUp],
  );

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      setFocus(true);
      userOnFocus?.(e as any);
    },
    [userOnFocus],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      setFocus(false);
      setPressed(false);
      userOnBlur?.(e as any);
    },
    [userOnBlur],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) {
        userOnKeyDown?.(e as any);
        return;
      }
      if (e.key === " " || e.key === "Enter") setPressed(true);
      userOnKeyDown?.(e as any);
    },
    [disabled, userOnKeyDown],
  );

  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === " " || e.key === "Enter") setPressed(false);
      userOnKeyUp?.(e as any);
    },
    [userOnKeyUp],
  );

  // base styles
  const computedStyle: React.CSSProperties = {
    backgroundColor: contract.colors.material,
    paddingTop: contract.space[7],
    paddingInline: contract.space[20],
    paddingBottom: contract.space[5],
    border: "none",
    color: disabled ? contract.colors.materialTextDisabled : contract.colors.materialText,
    fontSize: 12,
    // allow consumer styles to override base values
    ...style,
  };

  // hover
  if (hover && !pressed && !disabled && !noShadow) {
    computedStyle.boxShadow = `inset 1px 1px 0px 1px ${contract.colors.borderLightest}, inset 0 0 0 1px ${contract.colors.borderDark}, 1px 1px 0 0px ${contract.colors.borderDarkest}`;
  }

  // active/pressed
  if (pressed && !disabled) {
    computedStyle.paddingTop = contract.space[8];
    computedStyle.paddingInline = contract.space[20];
    computedStyle.paddingBottom = contract.space[4];
  }

  return (
    <Frame
      as={Component}
      {...props}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      style={computedStyle}
    />
  );
}
